import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';

const MarkAttendance = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [scanData, setScanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [todayAttendanceStatus, setTodayAttendanceStatus] = useState(null);

  useEffect(() => {
    if (!authLoading && user) {
      validateQrScan();
    }
  }, [uuid, user, authLoading]);

  useEffect(() => {
    if (!authLoading && user && (success || scanData?.alreadyMarked)) {
      fetchTodayAttendanceStatus();
    }
  }, [authLoading, user, success, scanData?.alreadyMarked]);

  const validateQrScan = async () => {
    if (!uuid) {
      setError('Invalid attendance link');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`/api/attendance/scan/${uuid}`);
      const data = response.data;
      setScanData(data);
      
      if (!data.valid) {
        setError(data.message || 'Invalid QR code');
      }
      
      trackEvent('attendance_qr_scanned', {
        event_category: 'Attendance',
        event_label: 'QR Scanned',
        valid: data.valid,
        already_marked: data.alreadyMarked
      });
    } catch (error) {
      console.error('Failed to validate QR scan:', error);
      setError('Failed to validate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayAttendanceStatus = async () => {
    try {
      const response = await axios.get('/api/attendance/today/status');
      setTodayAttendanceStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch today's attendance status:", error);
    }
  };

  const handleMarkAttendance = async () => {
    setMarkingAttendance(true);
    setError('');

    try {
      const response = await axios.post(`/api/attendance/mark/${uuid}`, { expectedCount: scanData?.attendanceCount || 0 });
      const data = response.data;
      
      if (data.success) {
        setSuccess(true);
        setSuccessMessage(data.message);
        fetchTodayAttendanceStatus();
        
        trackEvent('attendance_marked', {
          event_category: 'Attendance',
          event_label: 'Attendance Marked',
          already_marked: data.alreadyMarked,
          company_name: scanData?.companyName
        });
      } else {
        setError(data.message || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      setError(error.response?.data?.message || 'Failed to mark attendance. Please try again.');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const customerName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email;

  const renderAttendanceStatusScreen = ({ title, accentColor, accentBackground, icon, message }) => {
    const attendanceDate = todayAttendanceStatus?.attendanceDateDisplay || scanData?.attendanceDateDisplay || new Date().toLocaleDateString();
    const companyName = todayAttendanceStatus?.companyName || scanData?.companyName || 'N/A';
    const markedAtDisplay = todayAttendanceStatus?.markedAtDisplay;

    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: accentBackground,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '720px',
          margin: '0 auto',
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderRadius: '24px',
          padding: '32px 24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
          border: `3px solid ${accentColor}`,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '88px', lineHeight: 1, marginBottom: '18px' }}>{icon}</div>
          <h1 style={{ color: accentColor, marginBottom: '14px', fontSize: 'clamp(32px, 8vw, 56px)' }}>
            {title}
          </h1>
          <p style={{ color: 'var(--dark-blue)', marginBottom: '24px', fontSize: 'clamp(18px, 4.5vw, 28px)', lineHeight: '1.5', fontWeight: '600' }}>
            {message}
          </p>

          <div style={{
            background: '#F8FAFC',
            borderRadius: '18px',
            padding: '22px',
            border: '1px solid #CBD5E0',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: 'clamp(18px, 4.2vw, 26px)', color: 'var(--dark-blue)', lineHeight: '1.8' }}>
              <div><strong>Name:</strong> {todayAttendanceStatus?.customerName || customerName}</div>
              <div><strong>Date:</strong> {attendanceDate}</div>
              <div><strong>Club:</strong> {companyName}</div>
              {markedAtDisplay && <div><strong>Marked At:</strong> {markedAtDisplay}</div>}
            </div>
          </div>

          <div style={{ fontSize: 'clamp(16px, 3.8vw, 22px)', color: '#4A5568', marginTop: '22px', lineHeight: '1.6' }}>
            Please show this screen to the attendant.
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate('/dashboard')}
            style={{
              marginTop: '26px',
              width: '100%',
              maxWidth: '320px',
              fontSize: 'clamp(16px, 4vw, 22px)',
              padding: '16px 24px'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  };

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '48px', color: '#007bff' }}>📋</div>
        <div style={{ fontSize: '18px', color: '#333', fontWeight: '500' }}>
          Loading attendance system...
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    const currentPath = `/mark-attendance/${uuid}`;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    return null;
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '48px', color: '#007bff' }}>📋</div>
        <div style={{ fontSize: '18px', color: '#333', fontWeight: '500' }}>
          Validating attendance QR code...
        </div>
      </div>
    );
  }

  if (success) {
    return renderAttendanceStatusScreen({
      title: 'Attendance Marked',
      accentColor: '#2F855A',
      accentBackground: 'linear-gradient(180deg, #F0FFF4 0%, #E6FFFA 100%)',
      icon: '✅',
      message: successMessage || 'Attendance marked successfully'
    });
  }

  if (error || !scanData?.valid) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc3545', marginBottom: '15px', fontSize: '24px' }}>
            Cannot Mark Attendance
          </h2>
          <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
            {error || scanData?.message || 'Invalid attendance QR code'}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (scanData?.alreadyMarked) {
    return renderAttendanceStatusScreen({
      title: 'Attendance Already Marked',
      accentColor: '#2B6CB0',
      accentBackground: 'linear-gradient(180deg, #EBF8FF 0%, #F7FAFC 100%)',
      icon: '📋',
      message: scanData.message || 'Your attendance for today is already marked'
    });
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>📋</div>
        
        <h2 style={{ color: 'var(--dark-blue)', marginBottom: '15px', fontSize: '24px' }}>
          Mark Attendance
        </h2>
        
        <div style={{
          background: 'var(--pale-blue)',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '25px',
          border: '2px solid var(--primary-blue)'
        }}>
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
            {scanData.companyName}
          </h3>
          <p style={{ color: 'var(--gray-dark)', fontSize: '16px', marginBottom: '10px' }}>
            {scanData.message}
          </p>
          <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
            Date: {scanData.attendanceDateDisplay}
          </div>
        </div>

        <div>
          <button
            className="btn btn-primary"
            onClick={handleMarkAttendance}
            disabled={markingAttendance}
            style={{
              fontSize: '18px',
              padding: '15px 30px',
              background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
              border: 'none',
              marginBottom: '20px'
            }}
          >
            {markingAttendance ? 'Marking Attendance...' : '✅ Mark Attendance'}
          </button>
          
          <div style={{ fontSize: '14px', color: '#888' }}>
            <p>By marking attendance, you confirm your presence at {scanData.companyName}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkAttendance;
